import cn from 'classnames'
import React from 'react'

import { testProps } from 'utils/test/qaData'

import { CourseKitSize } from '../../CourseKitTypes'
import type { CourseKeyIconsType } from '../../IconCourse'
import IconCourse from '../../IconCourse'
import type { IButtonPropsType } from '../ButtonCourse'
import * as s from '../ButtonCourse.module.scss'

/**
 * Props for the IconButton component.
 */
export interface IIconButtonProps extends Omit<IButtonPropsType, 'children' | 'fluid'> {
  /**
   * The icon to display in the button.
   */
  icon: CourseKeyIconsType
  /**
   * The custom size of the icon inside button.
   */
  iconSize?: CourseKitSize
  /**
   * The style type of the button.
   */
  styleType?: 'primary' | 'secondary' | 'ghost' | 'accent' | 'free'
  /**
   * Optional test data attribute to be added to the IconButton.
   */
  testData?: string
  /**
   * Animation transform
   */
  transform?: React.CSSProperties['transform']
}

const IconButton = React.forwardRef<HTMLButtonElement, IIconButtonProps>(
  (
    {
      icon,
      iconSize,
      testData,
      name,
      active,
      size = CourseKitSize.M,
      disabled,
      type = 'button',
      inheritColor,
      transform,
      styleType = 'primary',
      ...htmlProps
    },
    ref,
  ) => {
    const { value, onClick, className, tabIndex, style, ...restHtmlProps } = htmlProps

    const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
      !disabled && onClick?.(e)

    const buttonClasses = cn(s.button, className, s[size], s[styleType], s[`icon-${size}`], {
      [s.disabled]: disabled,
      [s.inheritColor]: inheritColor,
    })

    return (
      <button
        {...restHtmlProps}
        {...testProps({ el: 'button', name, value, testData, disabled: Boolean(disabled) })}
        className={buttonClasses}
        name={name}
        onClick={handleClick}
        ref={ref}
        style={{ ...style, transform }}
        tabIndex={disabled ? -1 : tabIndex || 0}
        type={type}
        value={value}
      >
        <IconCourse name={icon} size={iconSize || size} aria-hidden />
      </button>
    )
  },
)

IconButton.displayName = 'IconButton'

export default IconButton
